import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCopy } from '../../copy/CopyProvider';
import { formatMoney } from '../../lib/format';
import { tierAvailability } from '../../pages/public/eventAvailability';
import type { EventDetail } from '../../lib/catalog';
import type { PublicAdmission } from '../../lib/admissions';
import { AdmissionForm } from './AdmissionForm';
import '../../pages/public/AdmissionStatus.css';

/**
 * The admission surface: the one place a visitor can get in.
 *
 * ── IT IS STILL THE EventTickets BLOCK ───────────────────────────────────────────────
 *
 * §34/§35. The CMS registry entry is unchanged, still called EventTickets, still `critical`,
 * still subject to the exactly-one publish invariant. What changed is what it renders
 * INSIDE: tiers for a ticketed event, an RSVP action for an RSVP one, a request action for a
 * request one, a waitlist action when full, an invitation prompt when invite-only.
 *
 * A versioned rename to EventAdmissions is a deliberate later step. Doing it here would have
 * meant migrating every published document and breaking the invariant that keeps a page from
 * silently having no way to buy anything — for a better name.
 *
 * ── THE MODE LIVES ON THE EVENT, NOT IN THE DOCUMENT ─────────────────────────────────
 *
 * §36/§90. This reads `event.admission`, resolved by the server. No CMS prop says
 * `mode: request`, because then two places would describe how an event admits people and a
 * template copied between events would carry the wrong one.
 */
export function AdmissionSurface({ event }: { event: EventDetail }) {
  const t = useCopy();
  const admission = event.admission ?? null;

  // No admission block on the payload at all: an older engine, or a cached response from
  // before this shipped. Ticketed is what every event did then, and is what it does now.
  const mode = admission?.mode ?? 'ticketed';

  if (mode === 'ticketed') return <TicketedSurface event={event} />;

  return (
    <div className="tickets glass-panel">
      <AdmissionPanel event={event} admission={admission} t={t} />
    </div>
  );
}

/** Exactly what the block rendered before this task, for exactly the events it rendered for. */
function TicketedSurface({ event }: { event: EventDetail }) {
  const t = useCopy();
  const anyBuyable = event.tiers.some((tier) => tierAvailability(tier).buyable);

  return (
    <div className="tickets glass-panel">
      <h2 className="tickets__title">{t('events.detail.tickets.heading', 'Tickets')}</h2>

      {event.tiers.length === 0 && (
        <p className="tickets__empty">
          {t('events.detail.tickets.empty', "Tickets for this event aren't on sale yet.")}
        </p>
      )}

      <ul className="tickets__list">
        {event.tiers.map((tier) => {
          const availability = tierAvailability(tier);
          return (
            <li
              key={tier.id}
              className={`tier glass-card${availability.buyable ? '' : ' tier--unavailable'}`}
            >
              <div className="tier__head">
                <span className="tier__name">{tier.name}</span>
                <span className="tier__price mono">{formatMoney(tier.priceCents)}</span>
              </div>
              {tier.description && <p className="tier__description">{tier.description}</p>}
              <span className={`tier__availability tier__availability--${availability.tone}`}>
                {availability.label}
              </span>
            </li>
          );
        })}
      </ul>

      {anyBuyable ? (
        <Link
          to={`/events/${event.id}/checkout`}
          className="button button--primary button--block tickets__cta"
        >
          {t('events.detail.cta.primary', 'Get tickets')}
        </Link>
      ) : (
        <button type="button" className="button button--primary button--block" disabled>
          {event.tiers.length === 0
            ? t('events.detail.cta.notOnSale', 'Not on sale')
            : t('events.detail.cta.soldOut', 'Sold out')}
        </button>
      )}
    </div>
  );
}

type CopyFn = ReturnType<typeof useCopy>;

/**
 * Everything that is not a ticket sale.
 *
 * §76: every state renders something meaningful. A blank critical panel is the failure this
 * whole mechanism exists to prevent, so the final branch is a sentence rather than nothing.
 */
function AdmissionPanel({
  event, admission, t,
}: { event: EventDetail; admission: PublicAdmission | null; t: CopyFn }) {
  const [open, setOpen] = useState<null | 'request' | 'rsvp' | 'waitlist' | 'invitation'>(null);

  const heading = useMemo(() => {
    if (!admission) return t('events.detail.tickets.heading', 'Tickets');
    if (admission.state === 'full' || admission.state === 'full_waitlist_open') {
      return t('admissions.full.heading', 'This event is full');
    }
    if (admission.mode === 'rsvp') return t('events.detail.tickets.heading', 'Tickets');
    return t('events.detail.tickets.heading', 'Tickets');
  }, [admission, t]);

  if (!admission) {
    return (
      <>
        <h2 className="tickets__title">{heading}</h2>
        <p className="tickets__empty">
          {t('events.detail.tickets.empty', "Tickets for this event aren't on sale yet.")}
        </p>
      </>
    );
  }

  if (open) {
    return (
      <AdmissionForm
        event={event}
        admission={admission}
        kind={open}
        onCancel={() => setOpen(null)}
      />
    );
  }

  return (
    <>
      <h2 className="tickets__title">{heading}</h2>

      {admission.state === 'not_open_yet' && (
        <>
          <p className="tickets__empty">{t('admissions.notOpenYet', 'Not open yet')}</p>
          <button type="button" className="button button--primary button--block" disabled>
            {t('admissions.notOpenYet', 'Not open yet')}
          </button>
        </>
      )}

      {admission.state === 'closed' && !admission.canJoinWaitlist && (
        <>
          <p className="tickets__empty">{t('admissions.closed', 'Closed')}</p>
          <button type="button" className="button button--primary button--block" disabled>
            {t('admissions.closed', 'Closed')}
          </button>
        </>
      )}

      {admission.canRsvp && (
        <>
          <p className="adm-surface__lede">
            {t('admissions.partySize.label', 'How many people?')}
            {admission.partySizeMax > 1 && ` Up to ${admission.partySizeMax}.`}
          </p>
          <button
            type="button"
            className="button button--primary button--block tickets__cta"
            onClick={() => setOpen('rsvp')}
          >
            {t('admissions.rsvp.cta', 'RSVP')}
          </button>
        </>
      )}

      {admission.canRequest && (
        <>
          <p className="adm-surface__lede">
            {t('admissions.request.received.body',
              "We'll review it and email you either way. Submitting a request does not "
              + 'guarantee admission.')}
          </p>
          <button
            type="button"
            className="button button--primary button--block tickets__cta"
            onClick={() => setOpen('request')}
          >
            {t('admissions.request.cta', 'Request tickets')}
          </button>
        </>
      )}

      {admission.requiresInvitation && admission.state === 'invitation_required' && (
        <>
          <p className="adm-surface__lede">
            {t('admissions.invite.required', 'This event is by invitation only.')}
          </p>
          <button
            type="button"
            className="button button--primary button--block tickets__cta"
            onClick={() => setOpen('invitation')}
          >
            {t('admissions.invite.cta', 'Redeem an invitation')}
          </button>
        </>
      )}

      {admission.canJoinWaitlist && (
        <>
          <p className="adm-surface__lede">
            {t('admissions.waitlist.joined.body', "We'll email you if a place opens up.")}
          </p>
          <button
            type="button"
            className={`button button--block tickets__cta ${
              admission.canRsvp || admission.canRequest ? 'button--ghost' : 'button--primary'}`}
            onClick={() => setOpen('waitlist')}
          >
            {t('admissions.waitlist.cta', 'Join the waitlist')}
          </button>
        </>
      )}

      {/* The last resort. §76: no blank critical panel, whatever state the event is in. */}
      {!admission.canRsvp && !admission.canRequest && !admission.canJoinWaitlist
        && !admission.requiresInvitation
        && admission.state !== 'not_open_yet' && admission.state !== 'closed' && (
        <>
          <p className="tickets__empty">
            {admission.state === 'full'
              ? t('admissions.full.heading', 'This event is full')
              : t('events.detail.tickets.empty', "Tickets for this event aren't on sale yet.")}
          </p>
          <button type="button" className="button button--primary button--block" disabled>
            {admission.state === 'full'
              ? t('events.detail.cta.soldOut', 'Sold out')
              : t('events.detail.cta.notOnSale', 'Not on sale')}
          </button>
        </>
      )}
    </>
  );
}
